import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Loader2, AlertCircle, CreditCard, Eye, Receipt, AlertTriangle, Wallet } from 'lucide-react';
import PaymentModal from '../../components/student-portal/PaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentLibrary() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({ issued_books: [], history: [], fine_summary: {} });
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const [libRes, gatewayRes] = await Promise.all([
        fetch(`${API_URL}/api/student-portal/library`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/payment/gateway-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (libRes.ok) {
        setData(await libRes.json());
      }
      if (gatewayRes.ok) {
        setGatewayConfig(await gatewayRes.json());
      }
    } catch (error) {
      console.error('Error fetching library data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayFine = (issueId, amount, bookTitle) => {
    setSelectedFine({
      id: issueId,
      amount: amount,
      description: `Library Fine - ${bookTitle}`
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    fetchData();
    setSelectedFine(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const fineDue = data.fine_summary?.due || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">Library</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Fine Summary with Pay Option */}
        {fineDue > 0 && (
          <Card className="mb-6 border-red-200 bg-gradient-to-r from-red-50 to-orange-50">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-red-800">Outstanding Library Fine</h3>
                    <p className="text-sm text-red-600">
                      Total: ₹{data.fine_summary?.total_fine || 0} | Paid: ₹{data.fine_summary?.paid || 0}
                    </p>
                    {gatewayConfig?.is_test_mode && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded mt-1">
                        <AlertTriangle className="w-3 h-3" /> Test Mode
                      </span>
                    )}
                  </div>
                </div>
                {gatewayConfig?.razorpay_enabled && (
                  <Button 
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => {
                      setSelectedFine({
                        id: null,
                        amount: fineDue,
                        description: "Library Fine Payment"
                      });
                      setShowPaymentModal(true);
                    }}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay ₹{fineDue.toLocaleString('en-IN')} Fine
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fine Summary Stats */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" /> Library Summary
              </span>
              <Button variant="outline" size="sm" onClick={() => navigate('/student/receipts')}>
                <Receipt className="w-4 h-4 mr-1" />
                All Receipts
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-500">Books Issued</p>
                <p className="text-2xl font-bold text-blue-600">{data.issued_books?.length || 0}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Total Fine</p>
                <p className="text-2xl font-bold">₹{data.fine_summary?.total_fine || 0}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-500">Fine Due</p>
                <p className="text-2xl font-bold text-red-600">₹{fineDue}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Currently Issued */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> Currently Issued Books
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.issued_books?.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No books currently issued</p>
            ) : (
              <div className="space-y-3">
                {data.issued_books?.map((book, idx) => {
                  const isOverdue = new Date(book.due_date) < new Date();
                  const fineAmount = book.fine || book.fine_amount || 0;
                  const isPaid = book.fine_paid === true || book.fine_paid > 0;
                  const finePending = isPaid ? 0 : fineAmount;
                  return (
                    <div key={idx} className={`p-4 rounded-lg ${isOverdue ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{book.book_title}</p>
                          <p className="text-sm text-gray-500">by {book.author || book.author_name || '-'}</p>
                          <p className="text-sm text-gray-500">Acc No: {book.accession_no || book.barcode || '-'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Issue Date</p>
                          <p className="font-medium">{book.issue_date}</p>
                          <p className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            Due: {book.due_date} {isOverdue && '(Overdue!)'}
                          </p>
                          {finePending > 0 && (
                            <div className="mt-2">
                              <span className="text-red-600 font-medium">Fine: ₹{finePending}</span>
                              {gatewayConfig?.razorpay_enabled && (
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  className="ml-2"
                                  onClick={() => handlePayFine(book.id, finePending, book.book_title)}
                                >
                                  Pay
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <Card>
          <CardHeader>
            <CardTitle>Issue History</CardTitle>
          </CardHeader>
          <CardContent>
            {data.history?.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No history found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Book</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Issue Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Return Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Fine</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.history?.map((item, idx) => {
                      const fineAmount = item.fine || item.fine_amount || 0;
                      const isPaid = item.fine_paid === true || item.fine_paid > 0;
                      const finePending = isPaid ? 0 : fineAmount;
                      return (
                        <tr key={idx}>
                          <td className="px-4 py-3 text-sm">{item.book_title}</td>
                          <td className="px-4 py-3 text-sm">{item.issue_date}</td>
                          <td className="px-4 py-3 text-sm">{item.return_date || item.returned_date || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              item.status === 'Returned' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            {fineAmount > 0 ? (
                              <span className={finePending > 0 ? 'text-red-600' : 'text-green-600'}>
                                ₹{fineAmount} {isPaid && '(Paid)'}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {finePending > 0 && gatewayConfig?.razorpay_enabled && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handlePayFine(item.id, finePending, item.book_title)}
                              >
                                Pay ₹{finePending}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Payment Modal */}
      {selectedFine && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedFine(null);
          }}
          feeType="library_fine"
          description={selectedFine.description}
          amount={selectedFine.amount}
          referenceId={selectedFine.id}
          isTestMode={gatewayConfig?.is_test_mode}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}
